import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import api from '../../services/api';
import { SOCKET_URL } from '../../config/api';
import { useI18n } from '../../i18n/useI18n';

type Props = {
  machineCode: string;
  userId?: string;
  /** Chỉ hiện toggle UI trên máy Pro */
  appProduct?: string | null;
};

const ACCENT = '#0f3460';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: alpha(ACCENT, 0.03),
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: 'transparent' },
    '&.Mui-focused fieldset': { borderColor: 'transparent' },
    '& input': {
      textAlign: 'center',
      fontWeight: 700,
      fontSize: { xs: '0.9rem', sm: '1rem' },
      color: ACCENT,
      py: { xs: 0.75, sm: 1 },
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    },
  },
};

type StepperFieldProps = {
  label: string;
  value: string;
  min: number;
  max: number;
  disabled?: boolean;
  onCommit: (next: number) => void;
};

const StepperField: React.FC<StepperFieldProps> = ({
  label,
  value,
  min,
  max,
  disabled = false,
  onCommit,
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const numeric = (() => {
    const n = Math.floor(Number(draft));
    if (!Number.isFinite(n)) return min;
    return Math.max(min, Math.min(max, n));
  })();

  const commit = (next: number) => {
    const clamped = Math.max(min, Math.min(max, Math.floor(Number(next))));
    if (!Number.isFinite(clamped)) return;
    setDraft(String(clamped));
    onCommit(clamped);
  };

  return (
    <Box
      sx={{
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          mb: 0.75,
          minHeight: { xs: 28, sm: 30 },
          fontWeight: 700,
          letterSpacing: { xs: '0.01em', sm: '0.04em' },
          textTransform: 'uppercase',
          color: alpha(ACCENT, 0.7),
          fontSize: { xs: '0.58rem', sm: '0.65rem' },
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Box
        display="flex"
        gap={{ xs: 0.35, sm: 0.75 }}
        alignItems="center"
        sx={{ minWidth: 0, mt: 'auto' }}
      >
        <IconButton
          size="small"
          disabled={disabled || numeric <= min}
          onClick={() => commit(numeric - 1)}
          sx={{
            width: { xs: 28, sm: 34 },
            height: { xs: 28, sm: 34 },
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: alpha(ACCENT, 0.06),
            color: ACCENT,
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          size="small"
          type="text"
          inputMode="numeric"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
          onBlur={() => commit(numeric)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            (e.target as HTMLInputElement).blur();
          }}
          inputProps={{
            inputMode: 'numeric',
            style: { textAlign: 'center', fontWeight: 700 },
          }}
          sx={{
            ...fieldSx,
            flex: 1,
            minWidth: 0,
            maxWidth: { sm: 72 },
          }}
        />
        <IconButton
          size="small"
          disabled={disabled || numeric >= max}
          onClick={() => commit(numeric + 1)}
          sx={{
            width: { xs: 28, sm: 34 },
            height: { xs: 28, sm: 34 },
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: alpha(ACCENT, 0.06),
            color: ACCENT,
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const MobileUploadDeviceLimit: React.FC<Props> = ({ machineCode, userId, appProduct }) => {
  const { pages: t } = useI18n();
  const [value, setValue] = useState(1);
  const [maxPrintCopies, setMaxPrintCopies] = useState(1);
  const [uiEnabled, setUiEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingUi, setSavingUi] = useState(false);
  const [message, setMessage] = useState('');
  const dirtyRef = useRef(false);
  const latestRef = useRef({ access: 1, prints: 1 });
  const showUiToggle = String(appProduct || '').trim().toLowerCase() === 'pro';

  const limitEndpoint = userId
    ? `/admin/users/${encodeURIComponent(userId)}/machines/${encodeURIComponent(machineCode)}/mobile-upload-device-limit`
    : `/machines/${encodeURIComponent(machineCode)}/mobile-upload-device-limit`;

  const uiEndpoint = userId
    ? `/admin/users/${encodeURIComponent(userId)}/machines/${encodeURIComponent(machineCode)}/mobile-upload-ui`
    : `/machines/${encodeURIComponent(machineCode)}/mobile-upload-ui`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const limitRes = await api.get(limitEndpoint);
      const max = limitRes.data?.mobileUploadMaxDeviceAccessPerDay == null
        ? 1
        : Math.max(0, Number(limitRes.data.mobileUploadMaxDeviceAccessPerDay) || 0);
      const printCopies = limitRes.data?.mobileUploadMaxPrintCopies == null
        ? 1
        : Math.max(1, Math.min(10, Number(limitRes.data.mobileUploadMaxPrintCopies) || 1));
      if (!dirtyRef.current) {
        setValue(max);
        setMaxPrintCopies(printCopies);
        latestRef.current = { access: max, prints: printCopies };
      }
      if (showUiToggle) {
        try {
          const uiRes = await api.get(uiEndpoint);
          setUiEnabled(uiRes.data?.mobileUploadUiEnabled !== false);
        } catch {
          /* giữ mặc định true — không chặn phần giới hạn */
        }
      }
    } catch {
      setMessage(t.deviceMgmt.limitLoadError);
    } finally {
      setLoading(false);
    }
  }, [limitEndpoint, uiEndpoint, showUiToggle, t.deviceMgmt.limitLoadError]);

  useEffect(() => {
    dirtyRef.current = false;
    void load();
  }, [load, machineCode]);

  useEffect(() => {
    const socket: Socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
    });
    const mc = String(machineCode).trim().toUpperCase();
    socket.on('connect', () => socket.emit('join-machine-room', mc));
    socket.on('mobile-upload:device-limit-changed', (data) => {
      if (
        String(data?.machineCode || '').toUpperCase()
        !== String(machineCode).toUpperCase()
      ) return;
      if (!dirtyRef.current && data?.mobileUploadMaxDeviceAccessPerDay != null) {
        const max = Math.max(0, Number(data.mobileUploadMaxDeviceAccessPerDay) || 0);
        setValue(max);
        latestRef.current.access = max;
      }
      if (!dirtyRef.current && data?.mobileUploadMaxPrintCopies != null) {
        const prints = Math.max(1, Math.min(10, Number(data.mobileUploadMaxPrintCopies) || 1));
        setMaxPrintCopies(prints);
        latestRef.current.prints = prints;
      }
    });
    socket.on('mobile-upload:ui-enabled-changed', (data) => {
      if (!showUiToggle) return;
      if (
        String(data?.machineCode || '').toUpperCase()
        !== String(machineCode).toUpperCase()
      ) return;
      if (typeof data?.mobileUploadUiEnabled === 'boolean') {
        setUiEnabled(data.mobileUploadUiEnabled);
      }
    });
    return () => {
      socket.emit('leave-machine-room', mc);
      socket.disconnect();
    };
  }, [machineCode, showUiToggle]);

  const persistLimits = useCallback(async (nextAccess: number, nextPrints: number) => {
    const access = Math.max(0, Math.min(9999, Math.floor(Number(nextAccess))));
    const prints = Math.max(1, Math.min(10, Math.floor(Number(nextPrints))));
    if (!Number.isFinite(access) || !Number.isFinite(prints)) {
      setMessage(t.deviceMgmt.accessInvalid);
      return;
    }
    if (
      access === latestRef.current.access
      && prints === latestRef.current.prints
    ) {
      return;
    }

    const prev = { ...latestRef.current };
    dirtyRef.current = true;
    setValue(access);
    setMaxPrintCopies(prints);
    latestRef.current = { access, prints };
    setMessage('');
    try {
      const res = await api.patch(limitEndpoint, {
        mobileUploadMaxDeviceAccessPerDay: access,
        mobileUploadMaxPrintCopies: prints,
        source: userId ? 'admin' : 'user',
      });
      // Bỏ qua response cũ nếu user đã chỉnh tiếp
      if (
        latestRef.current.access !== access
        || latestRef.current.prints !== prints
      ) {
        return;
      }
      const savedAccess = res.data?.mobileUploadMaxDeviceAccessPerDay == null
        ? access
        : Math.max(0, Number(res.data.mobileUploadMaxDeviceAccessPerDay) || 0);
      const savedPrints = res.data?.mobileUploadMaxPrintCopies == null
        ? prints
        : Math.max(1, Math.min(10, Number(res.data.mobileUploadMaxPrintCopies) || 1));
      setValue(savedAccess);
      setMaxPrintCopies(savedPrints);
      latestRef.current = { access: savedAccess, prints: savedPrints };
      dirtyRef.current = false;
    } catch (error: any) {
      if (
        latestRef.current.access !== access
        || latestRef.current.prints !== prints
      ) {
        return;
      }
      setValue(prev.access);
      setMaxPrintCopies(prev.prints);
      latestRef.current = prev;
      dirtyRef.current = false;
      setMessage(error?.response?.data?.message || t.deviceMgmt.limitSaveError);
    }
  }, [limitEndpoint, userId, t.deviceMgmt.accessInvalid, t.deviceMgmt.limitSaveError]);

  const onToggleUi = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSavingUi(true);
    setMessage('');
    const prev = uiEnabled;
    setUiEnabled(checked);
    try {
      const res = await api.patch(uiEndpoint, {
        mobileUploadUiEnabled: checked,
        source: userId ? 'admin' : 'user',
      });
      setUiEnabled(res.data?.mobileUploadUiEnabled !== false);
      setMessage(t.deviceMgmt.uiEnabledSaveSuccess);
    } catch (error: any) {
      setUiEnabled(prev);
      setMessage(error?.response?.data?.message || t.deviceMgmt.uiEnabledSaveError);
    } finally {
      setSavingUi(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: { xs: 1.25, sm: 2.25 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(ACCENT, 0.1),
        background: `linear-gradient(160deg, ${alpha('#fff', 0.98)} 0%, ${alpha(ACCENT, 0.03)} 100%)`,
        boxShadow: `0 4px 16px ${alpha(ACCENT, 0.04)}`,
      }}
    >
      {showUiToggle && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          pb: 1.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              color: ACCENT,
              letterSpacing: '-0.01em',
            }}
          >
            {t.deviceMgmt.uiEnabledTitle}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.82rem' }, lineHeight: 1.35, mt: 0.35 }}
          >
            {t.deviceMgmt.uiEnabledHint}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              mt: 0.75,
              px: 1,
              py: 0.25,
              borderRadius: 999,
              fontWeight: 700,
              fontSize: '0.7rem',
              bgcolor: uiEnabled ? alpha('#059669', 0.1) : alpha('#6b7280', 0.1),
              color: uiEnabled ? '#059669' : '#6b7280',
            }}
          >
            {uiEnabled ? t.deviceMgmt.uiEnabledOn : t.deviceMgmt.uiEnabledOff}
          </Typography>
        </Box>
        <Switch
          checked={uiEnabled}
          onChange={onToggleUi}
          disabled={loading || savingUi}
          color="primary"
          sx={{ flexShrink: 0 }}
        />
      </Box>
      )}

      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: '0.98rem', sm: '1.1rem' },
          color: ACCENT,
          letterSpacing: '-0.01em',
          mb: 0.5,
        }}
      >
        {t.deviceMgmt.limitTitle}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, fontSize: { xs: '0.78rem', sm: '0.875rem' }, lineHeight: 1.4 }}
      >
        {t.deviceMgmt.limitHint}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: { xs: 1, sm: 1.5 },
            alignItems: 'stretch',
            minWidth: 0,
          }}
        >
          <StepperField
            label={t.deviceMgmt.accessLabel}
            value={String(value)}
            min={0}
            max={9999}
            onCommit={(next) => { void persistLimits(next, maxPrintCopies); }}
          />
          <StepperField
            label={t.deviceMgmt.printCopiesLabel}
            value={String(maxPrintCopies)}
            min={1}
            max={10}
            onCommit={(next) => { void persistLimits(value, next); }}
          />
        </Box>
      )}

      {!!message && (
        <Alert
          severity={
            message === t.deviceMgmt.uiEnabledSaveSuccess
              ? 'success'
              : 'error'
          }
          sx={{ mt: 1.5 }}
          onClose={() => setMessage('')}
        >
          {message}
        </Alert>
      )}
    </Box>
  );
};

export default MobileUploadDeviceLimit;
